import { MutationOptions, useMutation } from '@tanstack/react-query';
import { App } from 'antd';
import { useShallow } from 'zustand/shallow';

import { api } from '@/api';
import { END_POINT } from '@/constants/end-point.constants';
import { useCommonStore } from '@/store/common.store';

import type { AccountRequest } from '../types';

const updateAccount = (id: string, requestBody: AccountRequest) =>
  api.put(END_POINT.accountsDetail(id), requestBody);

export const useUpdateAccount = () => {
  const { message } = App.useApp();
  const {
    setIsFormChanged,
    setIsProcessing,
    setIsFormUpdated,
    setMyAccountName,
  } = useCommonStore(
    useShallow(
      ({
        setIsFormChanged,
        setIsProcessing,
        setIsFormUpdated,
        setMyAccountName,
      }) => ({
        setIsFormChanged,
        setIsProcessing,
        setIsFormUpdated,
        setMyAccountName,
      }),
    ),
  );

  return useMutation({
    mutationFn: ({
      id,
      requestBody,
    }: {
      id: string;
      requestBody: AccountRequest;
      isUpdateMyAccount?: boolean;
    }) => updateAccount(id, requestBody),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess(_, { isUpdateMyAccount, requestBody: { name } }) {
      if (isUpdateMyAccount) setMyAccountName(name);
      setIsFormChanged(false);
      setIsFormUpdated(true);
      message.success('更新しました');
    },
  });
};

const deleteAccount = (id: number) =>
  api.delete(END_POINT.accounts, { data: { ids: [id] } });

export const useDeleteAccount = (
  mutationOptions?: MutationOptions<unknown, unknown, unknown>,
) => {
  const { message } = App.useApp();
  const setIsProcessing = useCommonStore(state => state.setIsProcessing);

  return useMutation({
    mutationFn: (id: number) => deleteAccount(id),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess(...arguments_) {
      mutationOptions?.onSuccess?.(...arguments_);
      message.success('削除しました');
    },
  });
};

const resendInvitationMail = (id: string) =>
  api.post(END_POINT.accountsResendInvitationMail(id));

export const useResendInvitationMail = (
  mutationOptions?: MutationOptions<unknown, unknown, unknown>,
) => {
  const { message } = App.useApp();
  const setIsProcessing = useCommonStore(state => state.setIsProcessing);

  return useMutation({
    mutationFn: (id: string) => resendInvitationMail(id),
    onMutate: () => setIsProcessing(true),
    onSettled: () => setIsProcessing(false),
    onSuccess(...arguments_) {
      mutationOptions?.onSuccess?.(...arguments_);
      message.success('招待メールを送信しました');
    },
  });
};
